import { useRef } from 'react';
import type { JSX } from 'react';
import { useSessionContext } from '../../contexts/SessionContext.js';
import type { TranslationKeys } from '../../i18n/types.js';
import { StudioBestiary, monsterLabel } from './StudioBestiary.js';
import { useI18n } from '../../i18n';
import { useStudioMapPanes } from './StudioMapPanes.js';

/** What a spawn needs of a monster (an SRD monster, or a row of the search index). */
export interface SpawnableMonster {
  readonly slug: string;
  readonly name: string;
}

/**
 * «＋ на карту» of Studio: the monster is added to the working map (the tab or «Карта рядом» the GM touched last) as
 * the drop of a drag would — srd:spawn-monster on THAT map. The Bestiary and the search (Ctrl+K) share it.
 */
export function useStudioMonsterSpawn(): {
  readonly targetId: string | null;
  readonly targetName: string | undefined;
  readonly spawn: (m: SpawnableMonster, count: number, withSheet?: boolean) => void;
} {
  const { mp, pages } = useSessionContext();
  const { locale } = useI18n();
  const panes = useStudioMapPanes();
  const targetId = panes.workingPageId;
  const targetName = targetId === null ? undefined : pages.pages.find((p) => p.id === targetId)?.name;
  // Cells a press has taken but the map has not shown yet (the next press must not pick the same one).
  const taken = useRef<Map<string, number>>(new Map());
  const spawn = (m: SpawnableMonster, count: number, withSheet = true): void => {
    if (targetId === null) return;
    const page = pages.getPageData(targetId);
    const grid = page?.gridSize ?? 70;
    const now = Date.now();
    for (const [key, at] of taken.current) if (now - at > 4000) taken.current.delete(key);
    const occupied = new Set<string>();
    for (const tk of page?.tokens ?? []) occupied.add(`${Math.round(tk.x / grid)}:${Math.round(tk.y / grid)}`);
    // GK-2: each next one stands in the next free cell (rows of 8 from the top-left), not on the same spot.
    for (let i = 0; i < count; i++) {
      let cell = 0;
      for (;; cell++) {
        const key = `${targetId}:${3 + (cell % 8)}:${3 + Math.floor(cell / 8)}`;
        if (!occupied.has(key.slice(targetId.length + 1)) && !taken.current.has(key)) {
          taken.current.set(key, now);
          break;
        }
      }
      mp.send({ type: 'srd:spawn-monster', slug: m.slug, x: grid * (3 + (cell % 8)), y: grid * (3 + Math.floor(cell / 8)), withSheet, name: monsterLabel(m.name, locale), pageId: targetId });
    }
  };
  return { targetId, targetName, spawn };
}

/**
 * The bestiary section of the Library with «＋ на карту» wired to Studio.
 * Rendered inside the map panes provider (the surface hands it to the rail as a node).
 */
export function StudioBestiaryConnected(props: { readonly t: (key: keyof TranslationKeys) => string; readonly embedded?: boolean }): JSX.Element {
  const { targetId, targetName, spawn } = useStudioMonsterSpawn();
  return <StudioBestiary t={props.t} embedded={props.embedded} spawn={{ targetName, canAdd: targetId !== null, onAdd: spawn }} />;
}

export default StudioBestiaryConnected;
